import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { useAuth, useLinks } from "hooks";
import { formatCurrency, formatDate, formatDecimal, formatNumber } from "utils";
import ListTable from "components/ListTable";
import { PrimaryLink } from "components/Links";
import { MissionLink } from "components/Missions";
import { fontSize, margins } from "style";
import { ICON_SIZE, ICON_TYPE, withIcon } from "components/Icon";
import { OrganizationLink } from "components/Organization";
import { UserLocation } from "components/User";

/**
 * RoleDetails
 *
 * @params {Object}   role
 * @params {Object}   mission
 * @params {String}   marginSize
 * @params {Boolean}  loading
 */
const RoleDetails = ({ role, mission, marginSize, loading, ...props }) => {
  const { isAdmin, canViewRolePublishedDate } = useAuth();
  const links = useLinks();

  const data = useMemo(
    () => (loading ? [] : TABLE_DATA(mission, role, links.mission(mission?.uuid), isAdmin, canViewRolePublishedDate)),
    [mission, role, isAdmin, canViewRolePublishedDate, loading]
  );

  return (
    <ListTable
      data={data}
      loading={loading}
      title={<FormattedMessage id="Missions.MissionRole.RoleDetails" />}
      secondaryTitle={
        <LinkRole
          css={styles.primary_link}
          href={links.public_mission_role(mission?.uuid, role?.slug)}
          target="_blank"
          withMoreIcon
        >
          <FormattedMessage id="Missions.MissionRole.PublicRoleLink" />
        </LinkRole>
      }
      marginSize={marginSize}
      resultsPerPage={data.length}
      {...props}
    />
  );
};

const TABLE_DATA = (mission, role, missionLinkTo, isAdmin, canViewRolePublishedDate) => [
  {
    label: <FormattedMessage id="Missions.MissionRole.Company" />,
    value: (
      <OrganizationLink
        data={{ ...mission?.organization, isConfidential: mission?.isOrganizationConfidential }}
        withOverlay={isAdmin}
        placement="auto"
      />
    ),
  },
  {
    label: <FormattedMessage id="Missions.MissionCard.MissionName" />,
    value: isAdmin ? (
      <MissionLink data={mission} placement="auto" withOverlay />
    ) : (
      <PrimaryLink to={missionLinkTo} routerEnabled>
        {mission?.name}
      </PrimaryLink>
    ),
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleTitle" />,
    value: role?.name,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleId" />,
    value: role?.slug,
  },
  ...[
    canViewRolePublishedDate && {
      label: "Published Date",
      value: formatDate(role?.publishedAt, "MMM DD, YYYY", true, true),
    },
  ].filter(Boolean),
  {
    label: <FormattedMessage id="Missions.MissionRole.ExpectedStartDate" />,
    value: moment(role?.startDate).format("MMMM Do, YYYY"),
  },
  {
    label: <FormattedMessage id="Global.Engagement" />,
    value: `${role?.engagementTime} hours/week`,
  },
  ...[
    role?.engagementType && {
      label: <FormattedMessage id="Missions.Roles.RoleDetails.EngagementType" />,
      value: <FormattedMessage id={`Missions.Roles.RoleDetails.EngagementType.${role?.engagementType}`} />,
    },
  ].filter(Boolean),
  {
    label: <FormattedMessage id="Global.Timezone" />,
    value: role?.timezone?.name || mission?.timezone?.name,
  },
  {
    label: <FormattedMessage id="Global.WorkArrangement" />,
    value: <FormattedMessage id={`Missions.MissionRole.WorkArrangement.${role?.workArrangement}`} />,
  },
  {
    label: <FormattedMessage id="Missions.Roles.RoleDetails.OnSiteLocation" />,
    value: <UserLocation data={role} />,
  },
  ...(isAdmin ? ADMIN_ONLY_FIELDS(role) : []),
];

const ADMIN_ONLY_FIELDS = (role) => [
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleCustomerBudget" />,
    value:
      role?.customerBudget?.rate?.min && role?.customerBudget?.rate?.min ? (
        <FormattedMessage
          id="Missions.MissionRole.CustomerBudget"
          values={{
            currency: role?.customerBudget?.currency,
            min: formatNumber(formatDecimal(role?.customerBudget?.rate?.min)),
            max: formatNumber(formatDecimal(role?.customerBudget?.rate?.max)),
            frequency: (
              <FormattedMessage
                id={`Missions.MissionRole.RoleCustomerBudget.Frequency${role?.customerBudget?.frequency}`}
              />
            ),
          }}
        />
      ) : (
        <FormattedMessage id="Global.EmDash" />
      ),
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleTargetRate" />,
    value: formatCurrency(role?.targetRate, role?.targetRateCurrency),
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleSalesOwner" />,
    value: role?.assignedTo?.name,
  },
  {
    label: <FormattedMessage id="Missions.MissionRole.RoleTalentOwner" />,
    value: role?.talentOwner?.name,
  },
];

const styles = {
  primary_link: css`
    font-size: ${fontSize.small};
    line-height: 1.3;
  `,
};

const LinkRole = withIcon(PrimaryLink, {
  type: ICON_TYPE.eye,
  size: ICON_SIZE.medium,
  color: "inherit",
});

RoleDetails.defaultProps = {
  marginSize: margins.normal,
};

RoleDetails.propTypes = {
  role: PropTypes.object,
  mission: PropTypes.object,
  marginSize: PropTypes.string,
  loading: PropTypes.bool,
};

export default RoleDetails;
